import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'

/**
 * Migration notice — this box is the DEV server and gets switched off once
 * active development stops. Everyone must move to the customer's permanent
 * install at `ai-creo-studio.onlytraf.com`.
 *
 * Mounted once in `routes/__root.tsx`, so it covers every route: the
 * authenticated Shell as well as the standalone login / register /
 * forgot-password / pending-approval pages.
 *
 * Fixed at the very top, 40px tall, `z-[600]` — above the Shell header
 * (`z-[500]`). Everything that positions itself against the viewport top
 * is offset by that 40px:
 *
 *   - `components/layout/Header.tsx`  → `top-[40px]`
 *   - `components/layout/Sidebar.tsx` → `top-[98px]`  (40 + 58 header)
 *   - `components/layout/Shell.tsx`   → `pt-[98px]`
 *   - the four standalone auth pages  → `pt-[72px]`   (40 + the 32px py-8)
 *
 * Grep for MIGRATION_BANNER_HEIGHT before touching any of those numbers.
 * When the dev server is finally retired, delete this file and revert
 * those five offsets in one commit.
 */
export const MIGRATION_BANNER_HEIGHT = 40

const TARGET_HOST = 'ai-creo-studio.onlytraf.com'
const TARGET_URL = `https://${TARGET_HOST}/`

/**
 * The banner (and the layout offsets it forces on Header / Sidebar / Shell /
 * the auth pages) only make sense on the temporary dev host — showing "this
 * server is temporary, move to ai-creo-studio.onlytraf.com" while already ON
 * ai-creo-studio.onlytraf.com is nonsensical. Hostname doesn't change without
 * a reload, so a plain function call (no state/effect) is enough.
 */
export function isMigrationBannerVisible(): boolean {
  if (typeof window === 'undefined') return true
  return window.location.hostname !== TARGET_HOST
}

export function MigrationBanner() {
  const { t } = useTranslation()

  if (!isMigrationBannerVisible()) return null

  return (
    <div
      role="status"
      className={
        'fixed top-0 left-0 right-0 z-[600] ' +
        'flex items-center justify-center gap-2 px-3 ' +
        'text-[13px] font-medium text-white text-center ' +
        'bg-gradient-to-r from-amber-600 to-orange-600 ' +
        'border-b border-amber-400/40 shadow-sm'
      }
      style={{ height: MIGRATION_BANNER_HEIGHT }}
    >
      <span className="truncate">
        {t('migration.notice', {
          defaultValue:
            'Цей сервер тимчасовий і скоро буде вимкнений. Основний робочий сервер:',
        })}
      </span>
      <a
        href={TARGET_URL}
        className={
          'inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 ' +
          'bg-white/20 underline underline-offset-2 ' +
          'hover:bg-white/30 transition-colors'
        }
      >
        ai-creo-studio.onlytraf.com
        <ArrowRight className="h-3.5 w-3.5" />
      </a>
    </div>
  )
}
